import type {
  ApolloQueryResult,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
} from '@apollo/client';
import { gql, useQuery as useApolloQuery } from '@apollo/client';
import { useCallback } from 'react';

import type { Gql } from '@transcend-io/type-utils';

import { logger } from '@main/core-ui/src/logger';
import type {
  AnySchema,
  EndpointParams,
  GqlObject,
  GraphQLResponse,
  ParamsToType,
  QueryEndpoint,
  ResponseToType,
} from '@main/schema-utils';
import { mkQueryString } from '@main/schema-utils';

/** The response from a `buildUseQuery` invocation. */
export type BuildUseQueryReturn<
  P extends EndpointParams,
  R extends GraphQLResponse | AnySchema,
> = (
  options?: QueryHookOptions<ResponseToType<R>, ParamsToType<P>>,
) => QueryResult<ResponseToType<R>, ParamsToType<P>>;

/**
 * Function to run a graphQL query
 */
export type QueryFunction<TParams extends EndpointParams, TResult> = (
  variables: ParamsToType<TParams>,
) => Promise<ApolloQueryResult<TResult>>;

/** The response from a `buildUseLazyQuery` invocation. */
export type BuildUseLazyQueryReturn<
  P extends EndpointParams,
  R extends GraphQLResponse | AnySchema,
> = (
  options?: QueryHookOptions<ResponseToType<R>, ParamsToType<P>>,
) => QueryFunction<P, ResponseToType<R>>;

/**
 * Create a GraphQL query hook from an endpoint definition
 * This is NOT lazy and will execute immediately on load
 *
 * @param endpoint - The GraphQL query endpoint definition
 * @param operationName - The GraphQL operation name
 * @param responseFields - Return a partial response
 * @param excludeResponseFields - The fields to exclude from the response
 * @returns A function that returns an Apollo query hook. The
 * returned hook is documented here:
 * https://www.apollographql.com/docs/react/api/react/hooks/#options
 */
export function buildUseQuery<
  TName extends string,
  TParams extends EndpointParams,
  TResult extends GraphQLResponse | AnySchema,
>(
  endpoint: QueryEndpoint<TName, TParams, TResult>,
  operationName?: string,
  responseFields?: GqlObject<TResult> | Gql<ResponseToType<TResult>>,
  excludeResponseFields?: string[],
): BuildUseQueryReturn<TParams, TResult> {
  const gqlTagInput: TypedDocumentNode<
    ResponseToType<TResult>,
    ParamsToType<TParams>
  > = gql`
    ${mkQueryString(
      endpoint,
      operationName,
      responseFields,
      undefined,
      excludeResponseFields,
    )}
  `;

  return (options) => {
    const {
      data,
      refetch: originalRefetch,
      fetchMore: originalFetchMore,
      ...response
    } = useApolloQuery(gqlTagInput, options);

    const refetch = useCallback(
      (options?: Partial<ParamsToType<TParams>>) =>
        Promise.resolve(originalRefetch(options)).then((res: any) => ({
          ...res,
          data: (res.data as any)?.[endpoint.name],
        })),
      [originalRefetch],
    );

    // TODO: https://linear.app/transcend/issue/WAL-5453 - use default `fetchMore` function, migrate to use field policies
    // See https://stackoverflow.com/questions/62742379/apollo-3-pagination-with-field-policies
    const fetchMore = useCallback(
      (fetchMoreOptions: any) =>
        originalFetchMore({
          ...fetchMoreOptions,
          updateQuery: !fetchMoreOptions.updateQuery
            ? undefined
            : (prev, { fetchMoreResult, ...rest }) => {
                const response = fetchMoreOptions.updateQuery(
                  prev ? (prev as any)[endpoint.name] : prev,
                  {
                    ...rest,
                    fetchMoreResult: fetchMoreResult
                      ? (fetchMoreResult as any)[endpoint.name]
                      : fetchMoreResult,
                  },
                );
                if (!response) {
                  return response;
                }
                return { [endpoint.name]: response };
              },
        }) as any,
      [originalFetchMore],
    );

    return {
      ...response,
      refetch,
      fetchMore,
      data: (data as any)?.[endpoint.name],
    };
  };
}

/**
 * Create a GraphQL query hook from an endpoint definition
 * This is lazy and will not execute immediately on load
 *
 * @param endpoint - The GraphQL query endpoint definition
 * @param operationName - The GraphQL operation name
 * @param responseFields - Partial response
 * @returns A hook that calls apollo's useQuery but with type enforcements
 */
export function buildUseLazyQuery<
  TName extends string,
  TParams extends EndpointParams,
  TResult extends GraphQLResponse | AnySchema,
>(
  endpoint: QueryEndpoint<TName, TParams, TResult>,
  operationName?: string,
  responseFields?: GqlObject<TResult> | Gql<ResponseToType<TResult>>,
): BuildUseLazyQueryReturn<TParams, TResult> {
  const gqlTagInput = gql`
    ${mkQueryString(endpoint, operationName, responseFields as any)}
  `;

  return (options) => {
    const { refetch } = useApolloQuery<
      ResponseToType<TResult>,
      ParamsToType<TParams>
    >(gqlTagInput, { ...options, skip: true });
    return (params: ParamsToType<TParams>) =>
      Promise.resolve(refetch(params))
        .then((result: any) => {
          if (result?.errors) {
            logger.error(result?.errors);
          }
          return {
            ...result,
            data: (result?.data as any)?.[endpoint.name],
          };
        })
        .catch((err) => {
          logger.error(err);
          throw err;
        });
  };
}
